import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { schema } from "@msuite/zero";
import type { Row } from "@rocicorp/zero";
import { client } from "@/hono";
import type { ExtendTicketCardProps, TicketCardProps } from "../ticket-card/ticket-card";
import { ProjectCard } from "./project-card";
import { ProjectCardId } from "./project-card-id";

/** The ticket row the board queries deliver: the synced columns plus the type that rides along. */
export type SyncedTicket = Row<typeof schema.tables.tickets> & {
	ticketType?: Row<typeof schema.tables.ticketTypes> | null;
};

/** Props Interface */
interface ProjectCardTicketIdProps {
	projectId: string;
	ticketId: string;
	isTicketDraggable?: boolean;
	ticketProps?: ExtendTicketCardProps;
	/**
	 * The synced rows a guest board renders from. On a foreign board the ticket and project
	 * detail endpoints refuse the caller, so a request there produces nothing but a failure
	 * toast per ticket, per refetch — when these are given, nothing is fetched at all. The
	 * caller's own board omits them and keeps the detail fetch it always had.
	 */
	ticket?: SyncedTicket;
	project?: Row<typeof schema.tables.projects>;
}

export function ProjectCardTicketId({
	ticketId,
	projectId,
	isTicketDraggable,
	ticketProps,
	ticket: syncedTicket,
	project: syncedProject,
}: ProjectCardTicketIdProps) {
	/** Hooks */
	const { data: ticket } = useQuery({
		queryKey: ["tickets", ticketId],
		enabled: !syncedTicket,
		queryFn: async () => {
			const response = await client.tickets[":ticketId"].$get({
				param: { ticketId },
			});
			const json = await responseHandler(response);
			return json.ticket;
		},
	});

	/** Render */
	if (syncedTicket) {
		// The synced row carries what the board renders — description, the type row, state, dates.
		// The cast bridges the card chain's wire types; every field the card reads beyond the
		// synced columns is guarded, and the context menu and file chips stay off on this path.
		//
		// `orgId` comes from the project because the synced ticket has no such column, and every
		// `update Ticket` rule conditions on it. This path is the guest board, so the organisation
		// it supplies is the **host's** — which is what makes the card's controls refuse here
		// rather than refuse for want of a field. The project card's own menu is the host's to
		// use, so it is not rendered at all.
		return (
			<ProjectCard
				project={syncedProject as Parameters<typeof ProjectCard>[0]["project"]}
				tickets={[
					{ ...syncedTicket, orgId: syncedProject?.orgId } as unknown as TicketCardProps["ticket"],
				]}
				isTicketDraggable={isTicketDraggable}
				ticketProps={ticketProps}
				withContextMenu={false}
			/>
		);
	}
	return (
		<ProjectCardId
			projectId={projectId}
			tickets={ticket ? [ticket] : []}
			isTicketDraggable={isTicketDraggable}
			ticketProps={ticketProps}
		/>
	);
}
